/**
 * @fileoverview Client-side logging utility
 * @description Sends browser logs to server /api/log endpoint for Vercel log drain capture
 *
 * @module lib/logger-client
 */

import { classifyClientError } from '~/lib/client-error-classification'
import { readClientDeploymentId } from '~/lib/client-error-recovery'
import { trackException } from '~/lib/gtag'
import { enrichClientErrorContext } from '~/lib/observability/client-errors'

type LogLevel = "info" | "warn" | "error"

interface LogEntry {
  level: LogLevel
  message: string
  event?: string
  context?: Record<string, unknown>
}

/** Discriminated rejection kinds for BetterStack filters. */
export type RejectionReasonType =
  | 'error'
  | 'undefined'
  | 'null'
  | 'string'
  | 'number'
  | 'boolean'
  | 'bigint'
  | 'object'
  | 'symbol'
  | 'function'

/**
 * Metadata captured from a non-Error promise rejection so production logs
 * don't collapse to the literal message `"undefined"`.
 */
export interface RejectionDiagnostics {
  /** Discriminated kind of the raw rejection value. */
  reasonType: RejectionReasonType
  /** Best-effort string form of the raw value (JSON for plain objects). */
  reasonValue?: string
  /** When the raw value is an Error, its constructor name. */
  reasonName?: string
}

const MAX_REASON_VALUE_LEN = 500

/** Max object depth when serializing rejection payloads for logs. */
const MAX_REJECTION_SANITIZE_DEPTH = 4

/** Max array elements retained when serializing rejection payloads. */
const MAX_REJECTION_ARRAY_ITEMS = 20

/**
 * The shared secret-shaped key list (regex alternatives) both sensitive-key
 * matchers below compose from, so they can never drift apart. `magic` covers
 * magic-link params/fields (`r3543268459`). Bare `key` is deliberately NOT
 * listed: the word-boundary rules would flip the documented-benign `sort_key`
 * example below, and credential-shaped key params are already covered
 * (`api_key`/`api-key`).
 */
const SENSITIVE_KEY_ALTERNATIVES =
  "token|secret|password|pwd|authorization|auth|cookie|credentials|magic|session|api[_-]?key|bearer|otp|signature|sig|refresh"

/**
 * Keys that may carry secrets in arbitrary rejection objects. Matched
 * case-insensitively with word boundaries so benign keys like `sort_key` survive.
 *
 * `code` is NOT in this object-key list: rejection objects legitimately carry
 * diagnostic error-code enums (`{code: 'AUTH'}`, `{code: 'ENOTFOUND'}`) whose
 * loss would blind triage — see the URL-param matcher below for where `code`
 * IS sensitive.
 */
const SENSITIVE_REJECTION_KEY_RE = new RegExp(
  `(?:^|_)(${SENSITIVE_KEY_ALTERNATIVES})(?:$|_)`,
  "i",
)

/**
 * Keys that may carry secrets as URL params (query or hash) — the object-key
 * list plus `code`, for OAuth authorization-code callbacks
 * (`/auth/callback?code=…`, `r3543268459`): a URL param named `code` on this
 * site is only ever the live exchange code, unlike an object field. The
 * `_`-boundary rules mean this also redacts benign snake_case params like
 * `?error_code=…` — an accepted cost (losing a numeric code from a logged URL
 * is cheap; draining a live exchange code is not).
 */
const SENSITIVE_PARAM_KEY_RE = new RegExp(
  `(?:^|_)(code|${SENSITIVE_KEY_ALTERNATIVES})(?:$|_)`,
  "i",
)

/**
 * Redact sensitive fields before serializing a rejection object to BetterStack.
 *
 * @param value - Raw rejection value.
 * @param depth - Current recursion depth (internal).
 * @returns Sanitized value safe for JSON.stringify in log context.
 */
function sanitizeRejectionPayload(value: unknown, depth = 0): unknown {
  if (depth > MAX_REJECTION_SANITIZE_DEPTH) {
    return '[Truncated]'
  }

  if (value === null || value === undefined) {
    return value
  }

  if (typeof value !== 'object') {
    return value
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_REJECTION_ARRAY_ITEMS)
      .map(item => sanitizeRejectionPayload(item, depth + 1))
  }

  const sanitized: Record<string, unknown> = {}
  for (const [key, fieldValue] of Object.entries(value as Record<string, unknown>)) {
    sanitized[key] = SENSITIVE_REJECTION_KEY_RE.test(key)
      ? '[Redacted]'
      : sanitizeRejectionPayload(fieldValue, depth + 1)
  }
  return sanitized
}

/**
 * Truncate serialized rejection payloads so a large API response object cannot
 * bloat BetterStack log volume.
 *
 * @param value - Serialized rejection value.
 * @returns Truncated string safe for log context.
 */
function truncateReasonValue(value: string): string {
  if (value.length <= MAX_REASON_VALUE_LEN) {
    return value
  }
  return `${value.slice(0, MAX_REASON_VALUE_LEN)}…`
}

/**
 * Redact sensitive query-string values (magic-link `token`, `secret`, …) from a
 * full URL before it's logged.
 *
 * Client faults capture `window.location.href`; on a credentialed route like
 * `/auth/verify?token=…` that would otherwise ship a LIVE magic-link token to
 * BetterStack — precisely when a crash means the user can't consume it. We keep
 * `url` useful (non-secret params survive) rather than dropping to bare
 * `pathname`, redacting only param names that match
 * {@link SENSITIVE_PARAM_KEY_RE}. The hash FRAGMENT is covered too —
 * OAuth/implicit-grant style flows put credentials there (`#access_token=…`),
 * and fragments never reach the server, so nothing upstream can have scrubbed
 * them. A plain scroll-anchor hash (`#pricing`) carries no `key=value`
 * assignments matching a sensitive key, so it survives byte-for-byte. A
 * non-parseable value is returned as-is.
 */
export function redactSensitiveUrl(href: string): string {
  try {
    const url = new URL(href)
    let redacted = false
    for (const key of [...url.searchParams.keys()]) {
      if (SENSITIVE_PARAM_KEY_RE.test(key)) {
        url.searchParams.set(key, "[Redacted]")
        redacted = true
      }
    }
    if (url.hash.length > 1) {
      const fragment = url.hash.slice(1)
      // Hash-ROUTED URLs (`#/auth/callback?token=…`) embed a route path plus a
      // query inside the fragment; parsing the whole fragment as params would
      // glue the path onto the first key (`/auth/callback?token`), which the
      // word-boundary regex correctly refuses — and the token would survive.
      // Split at the first `?`: before it is the route path (kept verbatim),
      // after it is the query to redact. A plain `#key=value` fragment has no
      // `?`, so the entire fragment is treated as params, as before.
      const queryStart = fragment.indexOf("?")
      const hashPath = queryStart === -1 ? "" : fragment.slice(0, queryStart)
      const paramsText = queryStart === -1 ? fragment : fragment.slice(queryStart + 1)
      const hashParams = new URLSearchParams(paramsText)
      let hashRedacted = false
      for (const key of [...hashParams.keys()]) {
        if (SENSITIVE_PARAM_KEY_RE.test(key)) {
          hashParams.set(key, "[Redacted]")
          hashRedacted = true
        }
      }
      // Only rewrite the hash when something was redacted: URLSearchParams
      // round-trips a scroll anchor like `#pricing` as `pricing=`, so an
      // unconditional rewrite would corrupt benign anchors.
      if (hashRedacted) {
        url.hash = `#${hashPath}${queryStart === -1 ? "" : "?"}${hashParams.toString()}`
        redacted = true
      }
    }
    return redacted ? url.toString() : href
  } catch {
    return href
  }
}

/**
 * A `?key=value` / `#key=value` / `&key=value` assignment inside free text.
 * The value stops at characters that end a URL param in practice (whitespace,
 * the next `&`/`#`, quotes, brackets). Colons are INCLUDED in the value — a
 * credential like `token=abc:def` must be consumed whole, not split at the
 * first `:` leaving `:def` behind. For stack-trace text only, a frame's
 * trailing digits-only `:line:col` suffix is peeled back off in
 * {@link redactSensitiveText} via its `preserveLineCol` option.
 */
const TEXT_PARAM_ASSIGNMENT_RE = /([?#&])([A-Za-z0-9_-]+)=([^\s&#"'`<>()[\]]*)/g

/**
 * A stack frame's trailing `:line:col` (or `:line`) suffix — digits-only colon
 * groups at the end of a matched param value. Preserved through redaction ONLY
 * for stack text (`preserveLineCol`, below) so frame locations stay
 * diagnosable; in every other context the whole colon-bearing value is
 * consumed as part of the credential.
 */
const TRAILING_LINE_COL_RE = /(?::\d+)+$/

/**
 * Redact sensitive query/hash param VALUES embedded in free text — stack
 * traces and `window.onerror` filenames.
 *
 * A frame from an inline script embeds the full page URL
 * (`at https://…/auth/verify?token=…:1:2`), so a crash on a credentialed route
 * would ship the live secret to the drain through `stack`/`filename` even
 * though the structured `url` field is redacted. Matches param assignments and
 * replaces the value with `[Redacted]` when the key matches
 * {@link SENSITIVE_PARAM_KEY_RE}; all other text (frame locations,
 * benign params, scroll anchors) is left untouched.
 *
 * INTENTIONAL BOUNDS — this is a `key=value` heuristic, not a secret scanner.
 * Out of scope by design (each is pinned by a negative test so a future change
 * that widens the net does so deliberately):
 *  - `Authorization: Bearer <jwt>` header text (no `=` assignment);
 *  - bare JWT-shaped or high-entropy values with no sensitive key attached;
 *  - path-segment secrets (`/reset/<token>` — the secret is not a param).
 * Those shapes should not reach client error text on this site's routes; if
 * one ever does, widen the net here (entropy/JWT detection) rather than at a
 * call site.
 *
 * @param text - Free text possibly embedding URL param assignments.
 * @param options.preserveLineCol - Set ONLY for stack-trace text, where a
 * matched value ends in a frame's `:line:col` suffix that should stay
 * diagnosable. Messages / filenames / context strings have no frame suffix,
 * so for them the entire colon-bearing value is a credential and is consumed
 * whole — peeling there would re-leak a numeric tail (`token=abc:123` must not
 * drain as `token=[Redacted]:123`).
 */
export function redactSensitiveText(
  text: string,
  options: { preserveLineCol?: boolean } = {},
): string {
  return text.replace(
    TEXT_PARAM_ASSIGNMENT_RE,
    (match, separator: string, key: string, value: string) => {
      if (!SENSITIVE_PARAM_KEY_RE.test(key)) {
        return match
      }
      // Stack text only: peel the frame's digits-only `:line:col` suffix back
      // off the consumed value so frame locations stay diagnosable. Residual
      // ambiguity in that mode: a secret whose tail is itself digits-only
      // colon groups keeps that low-entropy numeric tail — accepted so real
      // `:line:col` suffixes survive. Everywhere else the whole colon-bearing
      // value is consumed (`token=abc:def` used to leak `:def` when split at
      // the first colon).
      const lineCol = options.preserveLineCol
        ? TRAILING_LINE_COL_RE.exec(value)?.[0] ?? ""
        : ""
      return `${separator}${key}=[Redacted]${lineCol}`
    },
  )
}

/**
 * Normalize an unhandled promise rejection into an Error plus diagnostics.
 *
 * Bare `Promise.reject()` / `reject(undefined)` used to surface as
 * `message: "undefined"` in BetterStack. This helper preserves the original
 * shape so the next occurrence is diagnosable.
 *
 * @param reason - The raw `event.reason` from a `PromiseRejectionEvent`.
 * @returns The Error to log and structured diagnostics for the context payload.
 */
export function normalizeUnhandledRejection(reason: unknown): {
  error: Error
  diagnostics: RejectionDiagnostics
} {
  if (reason instanceof Error) {
    return {
      error: reason,
      diagnostics: {
        reasonType: 'error',
        reasonName: reason.name,
      },
    }
  }

  if (reason === undefined) {
    return {
      error: new Error('Unhandled promise rejection (reason was undefined)'),
      diagnostics: {
        reasonType: 'undefined',
      },
    }
  }

  if (reason === null) {
    return {
      error: new Error('Unhandled promise rejection (reason was null)'),
      diagnostics: {
        reasonType: 'null',
      },
    }
  }

  const reasonType = typeof reason as RejectionReasonType

  if (reasonType === 'string') {
    const text = reason as string
    return {
      error: new Error(text.length > 0 ? text : 'Unhandled promise rejection (empty string)'),
      diagnostics: {
        reasonType: 'string',
        reasonValue: text,
      },
    }
  }

  if (reasonType === 'number' || reasonType === 'boolean' || reasonType === 'bigint') {
    const text =
      reasonType === 'number'
        ? (reason as number).toString()
        : reasonType === 'boolean'
          ? (reason as boolean).toString()
          : (reason as bigint).toString()
    return {
      error: new Error(`Unhandled promise rejection: ${text}`),
      diagnostics: {
        reasonType,
        reasonValue: text,
      },
    }
  }

  if (reasonType === 'object') {
    const record = reason as Record<string, unknown>
    const messageField = record.message
    const message =
      typeof messageField === 'string' && messageField.length > 0
        ? messageField
        : undefined

    let serialized: string | undefined
    try {
      serialized = JSON.stringify(sanitizeRejectionPayload(reason))
    } catch {
      serialized = Object.prototype.toString.call(reason)
    }

    const reasonValue = serialized ? truncateReasonValue(serialized) : undefined

    return {
      error: new Error(message ?? `Unhandled promise rejection: ${serialized ?? '[object]'}`),
      diagnostics: {
        reasonType: 'object',
        reasonName: typeof record.name === 'string' ? record.name : undefined,
        reasonValue,
      },
    }
  }

  const fallbackText =
    reasonType === 'symbol'
      ? (reason as symbol).toString()
      : reasonType === 'function'
        ? (reason as { name?: string }).name ?? 'anonymous function'
        : reasonType

  return {
    error: new Error(`Unhandled promise rejection (${reasonType})`),
    diagnostics: {
      reasonType,
      reasonValue: truncateReasonValue(fallbackText),
    },
  }
}

/**
 * Well-known benign browser errors that are NOT actionable application faults but
 * fire across every page (and every site), so they must not reach the log drain:
 *
 *  - `ResizeObserver loop …` — emitted when a ResizeObserver callback doesn't
 *    settle within one frame. Benign by spec; every browser produces it.
 *  - Errors THROWN from a browser-extension-injected script (the innermost stack
 *    frame is an `*-extension://` URL) — third-party code we don't control.
 *
 * (Opaque cross-origin "Script error." events carry no usable message/stack at
 * this layer, so they're filtered at the window-handler level in
 * `BetterStackLogProvider`.)
 */
const IGNORABLE_CLIENT_ERROR_PATTERNS: readonly RegExp[] = [
  /ResizeObserver loop (?:limit exceeded|completed with undelivered notifications)/,
]

const EXTENSION_FRAME_RE =
  /(?:chrome-extension|moz-extension|safari-web-extension|safari-extension):\/\//

/**
 * Whether the error was THROWN from a browser extension — i.e. its INNERMOST
 * stack frame (where it originated) is an extension URL. We deliberately check
 * only the innermost frame, not the whole stack: a privacy/ad-block extension
 * that monkey-patches `fetch`/`Promise` leaves an extension frame in the OUTER
 * stack of a genuine application error, and that error must still reach the
 * drain. The innermost frame is the first stack line carrying a URL (V8: the
 * line after "Error: …"; SpiderMonkey: the first `fn@url` line).
 */
function isThrownFromExtension(stack: string): boolean {
  const innermostFrame = stack
    .split('\n')
    .map(line => line.trim())
    .find(line => line.includes('://'))
  return innermostFrame !== undefined && EXTENSION_FRAME_RE.test(innermostFrame)
}

/**
 * Whether a client error is well-known benign browser/extension noise that should
 * be dropped before it POSTs to `/api/log`. Centralized here so both window
 * handlers and any error boundary that calls {@link logClientError} share one
 * ignore-list.
 *
 * @param message - The error's message.
 * @param stack - The error's stack, when available.
 * @returns `true` if the error should be silently ignored.
 */
export function shouldIgnoreClientError(message: string, stack?: string): boolean {
  if (IGNORABLE_CLIENT_ERROR_PATTERNS.some(pattern => pattern.test(message))) {
    return true
  }
  return stack !== undefined && isThrownFromExtension(stack)
}

/**
 * Send log entry to server /api/log endpoint
 * Server will output to console → Vercel log drain → BetterStack
 *
 * @returns Whether the ingest succeeded (`response.ok`). Failures (network
 * throw, 429 rate limit, 5xx) resolve `false` so callers can roll back dedupe
 * state — the send itself stays fire-and-forget-safe (never throws).
 */
async function sendLog(entry: LogEntry): Promise<boolean> {
  try {
    const response = await fetch("/api/log", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(entry),
      // Use keepalive to ensure log is sent even if page is closing
      keepalive: true,
    })
    return response.ok
  } catch (error) {
    // Silently fail - we don't want logging errors to break the app
    if (process.env.NODE_ENV === "development") {
      console.warn("[logger-client] Failed to send log:", error)
    }
    return false
  }
}

/**
 * Client-side logger that sends logs to server
 * All logs go through /api/log → server console → Vercel log drain → BetterStack
 */
export const clientLogger = {
  info: (message: string, context?: Record<string, unknown>) => {
    void sendLog({ level: "info", message, context })
  },

  warn: (message: string, context?: Record<string, unknown>) => {
    void sendLog({ level: "warn", message, context })
  },

  error: (message: string, context?: Record<string, unknown>) => {
    void sendLog({ level: "error", message, context, event: "client_error" })
  },
}

/** Per-session dedupe window for identical client errors (ms). */
const CLIENT_ERROR_DEDUPE_MS = 60_000

/** Recently logged client error fingerprints → timestamp. */
const recentClientErrorFingerprints = new Map<string, number>()

/**
 * Clears the in-memory dedupe map. Intended for unit tests only.
 *
 * @returns {void}
 */
export function resetClientErrorDedupeForTests(): void {
  recentClientErrorFingerprints.clear()
}

/**
 * Returns whether an identical client error was logged recently.
 *
 * @param {string} fingerprint - Stable error fingerprint.
 * @returns {boolean} True when the error should be suppressed as a duplicate.
 */
function isDuplicateClientError(fingerprint: string): boolean {
  const now = Date.now()
  const lastSeen = recentClientErrorFingerprints.get(fingerprint)
  if (lastSeen !== undefined && now - lastSeen < CLIENT_ERROR_DEDUPE_MS) {
    return true
  }
  recentClientErrorFingerprints.set(fingerprint, now)

  // Bound map size so long sessions cannot grow memory without limit.
  if (recentClientErrorFingerprints.size > 100) {
    const cutoff = now - CLIENT_ERROR_DEDUPE_MS
    for (const [key, seenAt] of recentClientErrorFingerprints) {
      if (seenAt < cutoff) {
        recentClientErrorFingerprints.delete(key)
      }
    }
  }

  return false
}

/**
 * Log a client error with full context.
 *
 * @param {unknown} error - Error instance or serializable rejection value.
 * @param {Record<string, unknown>} [context] - Additional structured context.
 * @returns {boolean} True when a log was emitted; false when ignored or deduped.
 */
export function logClientError(
  error: unknown,
  context: Record<string, unknown> = {}
): boolean {
  const normalizedError =
    error instanceof Error ? error : new Error(String(error))
  const errorDetails = {
    name: normalizedError.name,
    // The message can embed a URL too (`Failed to load https://…?api_key=…`),
    // so it gets the same treatment as the stack below. Classification /
    // enrichment / dedupe all read the RAW `normalizedError`, so redacting the
    // drained copy changes no routing or dedupe behavior.
    message: redactSensitiveText(normalizedError.message),
    // Inline-script frames embed the full page URL, so a raw stack can carry
    // `?token=…` / `#access_token=…` into the drain even though the structured
    // `url` field is redacted. Redaction only rewrites sensitive param VALUES,
    // so frame locations stay intact (and the extension-origin check below
    // still sees the scheme it matches on).
    // `preserveLineCol` is passed for the stack ONLY: its matched values can
    // end in a frame's `:line:col` suffix worth keeping. Message / filename /
    // context strings get the default whole-value consumption.
    stack:
      normalizedError.stack !== undefined
        ? redactSensitiveText(normalizedError.stack, { preserveLineCol: true })
        : undefined,
  }

  // Drop well-known benign browser/extension noise (ResizeObserver loops,
  // extension-injected script errors) before it reaches the log drain, so
  // /api/log volume stays scoped to actionable application faults.
  if (shouldIgnoreClientError(errorDetails.message, errorDetails.stack)) {
    return false
  }

  const classification = classifyClientError(normalizedError)

  // EVERY caller-supplied string context value gets the same text redaction as
  // the stack — not just `filename` (`r3543268454`): rejection diagnostics like
  // `reasonValue` (spread in via `handleClientError` from
  // `normalizeUnhandledRejection`) carry the raw rejection text, which embeds
  // the same `?token=…` URLs the message does. A blanket pass is safe because
  // `redactSensitiveText` only rewrites `?/#/&key=value` assignments whose key
  // is sensitive — benign enums (`type: "runtime_error"`), component tags, and
  // digests pass through byte-for-byte. Applied BEFORE enrichment so the dedupe
  // fingerprint (which folds `filename` in — see `buildClientErrorFingerprint`)
  // is built from the redacted value and stays stable across occurrences.
  const safeContext = Object.fromEntries(
    Object.entries(context).map(([key, value]) => [
      key,
      typeof value === "string" ? redactSensitiveText(value) : value,
    ]),
  )

  const enriched = enrichClientErrorContext(normalizedError, {
    ...safeContext,
    url:
      typeof window !== "undefined"
        ? redactSensitiveUrl(window.location.href)
        : undefined,
    userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
  })

  const fingerprint =
    typeof enriched.errorFingerprint === 'string'
      ? enriched.errorFingerprint
      : undefined

  if (fingerprint && isDuplicateClientError(fingerprint)) {
    return false
  }

  // Merge the two category taxonomies: the enrichment's hydration/boundary/
  // rejection buckets are finer than `classifyClientError`'s single `hydration`
  // family, so they win; every GENERIC enrichment bucket (`unknown`,
  // `runtime_error`, `react_runtime`) defers to the classifier — which emits
  // the pre-existing BetterStack filter values (`application`, `chunk_load`,
  // `dom_reconciliation`, `hydration`) that dashboards already match on. The
  // react-ness of a fault is carried by `reactErrorCode`, not the category.
  const errorCategory =
    enriched.errorCategory === 'unknown' ||
    enriched.errorCategory === 'runtime_error' ||
    enriched.errorCategory === 'react_runtime'
      ? classification.errorCategory
      : enriched.errorCategory

  const recoveryBlocked = context.recoveryBlocked === true
  const recoveryStorageUnavailable = context.recoveryStorageUnavailable === true
  const recoveryAttempted = context.recoveryAttempted === true
  // Downgrade to `warn` only when a recoverable fault actually triggered
  // auto-recovery. Boundary/manual-reload paths keep the enrichment's severity
  // (`error`, or `warn` for recovered hydration).
  const level: LogLevel =
    classification.recoverable &&
    recoveryAttempted &&
    !recoveryBlocked &&
    !recoveryStorageUnavailable
      ? 'warn'
      : enriched.logLevel

  const logMessage =
    typeof enriched.reactErrorDecoded === 'string'
      ? enriched.reactErrorDecoded
      : errorDetails.message

  void sendLog({
    level,
    message: logMessage,
    event: enriched.clientEvent,
    context: {
      ...errorDetails,
      ...classification,
      ...enriched,
      // `classifyClientError` emits `reactErrorCode` as a STRING ('418') and
      // pre-existing BetterStack filters match on that shape; the enrichment's
      // numeric code (spread above) must not change the emitted field type.
      ...(enriched.reactErrorCode !== undefined
        ? { reactErrorCode: String(enriched.reactErrorCode) }
        : {}),
      // The fingerprint's location falls back to a raw stack frame (see
      // `buildClientErrorFingerprint`) when `filename` is absent, so it can
      // embed the same `?token=…` a stack does. Redact the DRAINED copy only —
      // in-memory dedupe keeps using the enrichment's value, and redaction is
      // deterministic, so dedupe behavior is unchanged.
      ...(fingerprint !== undefined
        ? { errorFingerprint: redactSensitiveText(fingerprint) }
        : {}),
      errorCategory,
      logLevel: level,
      // The clean route path — group #418s by this in BetterStack to see which
      // pages mismatch (the full `url` carries query strings, so it's noisy).
      pathname: typeof window !== "undefined" ? window.location.pathname : enriched.pathname,
      clientDeploymentId: readClientDeploymentId(),
    },
  }).then(sent => {
    if (!sent) {
      // The fingerprint was recorded optimistically (the caller needs a sync
      // answer, and on unload paths the response never arrives). If the drain
      // POST is KNOWN to have failed (network throw, 429, 5xx), roll it back
      // so the next occurrence inside the dedupe window retries instead of
      // being silently swallowed for 60s.
      if (fingerprint !== undefined) {
        recentClientErrorFingerprints.delete(fingerprint)
      }
      return
    }

    // The ONE GA4 exception mirror. Every drained client error is mirrored
    // here (and ONLY here — call sites must not call `trackException` for a
    // fault they also drain, or one crash emits two GA4 exceptions), only
    // after the drain CONFIRMED the ingest — mirroring off the synchronous
    // "attempted" flag could emit GA-only `exception` events in a loop where
    // the drain keeps rejecting (e.g. rate-limited) while the fingerprint
    // rollback above re-arms each retry. The trade-off: an unload-path send
    // (`keepalive`) whose response never arrives isn't mirrored — a small GA4
    // undercount, never a BetterStack one. Consent gating lives inside
    // `trackException`. Context contract:
    //  - `analyticsMirror: false` opts a drained-but-expected fault out of the
    //    GA4 beacon (e.g. blog not-found control flow).
    //  - `fatal: true` marks a fatal crash (root-layout `global-error`).
    //  - `digest` / `errorId` carry the correlating id into GA4's `error_id`.
    if (context.analyticsMirror === false) {
      return
    }
    const hydration =
      enriched.isHydrationMismatch === true || enriched.isHydrationRecovered === true
    const component =
      typeof context.component === 'string'
        ? context.component
        : hydration
          ? 'hydration'
          : typeof context.type === 'string'
            ? context.type
            : 'client'
    const errorId =
      typeof context.digest === 'string'
        ? context.digest
        : typeof context.errorId === 'string'
          ? context.errorId
          : undefined
    trackException({
      description: logMessage,
      fatal: context.fatal === true,
      component,
      ...(errorId !== undefined ? { errorId } : {}),
    })
  })

  return true
}
